import type {
  SelectionList,
  SelectionListIdNames,
  ValidValueTypes,
} from '@revelio/iso-utility';

import type { TreeData } from '../../components/tree/useTreeApi';
import type { TaxonomyLevelMetadata } from './taxonomyTree';
import { treeToParentLinkedSelectionLists } from './treeToParentLinkedSelectionLists';

export const CUSTOM_SKILL_LIST_PREFIX = 'custom_skill_k';

// Every custom-skill level tag is `skill_k<N>` (k150, k1500, …). The report
// vocabulary names the same level `custom_skill_k<N>`, so the mapping is the
// shared k-number — derived per level, never hard-coded to a fixed set of
// levels. Add or drop a level in the skill tree and the picker follows.
const customSkillListId = (tag: string | null | undefined): string | null => {
  const k = tag?.match(/_k(\d+)$/i)?.[1];
  return k ? `${CUSTOM_SKILL_LIST_PREFIX}${k}` : null;
};

/**
 * Flattens a custom-skill TaxonomyTree into the report builder's per-level
 * selection lists (`custom_skill_k150` → `custom_skill_k15000` …) that drive
 * the BreadcrumbTree picker. Parent-linked and title-carrying via
 * treeToParentLinkedSelectionLists; ordered shallow→deep so the breadcrumb
 * renders top level first. Works for any number of levels the tree exposes.
 */
export const treeToCustomSkillSelectionLists = (
  treeData: readonly TreeData[],
  levels: readonly TaxonomyLevelMetadata[]
): SelectionList<ValidValueTypes>[] => {
  const itemsByListId = treeToParentLinkedSelectionLists(
    treeData,
    levels,
    (level) => customSkillListId(level.tag)
  );

  return [...levels]
    .sort((a, b) => a.id - b.id)
    .flatMap((level) => {
      const listId = customSkillListId(level.tag);
      if (listId === null) return [];
      return [
        {
          id: listId as SelectionListIdNames,
          label: level.label ?? listId,
          value: itemsByListId[listId] ?? [],
        } as SelectionList<ValidValueTypes>,
      ];
    });
};
